import React, { useEffect, useState } from "react";
import {
  X, MagicWand, Waveform, SpeakerHigh, Ear, ArrowCounterClockwise,
  Heart, Trash, Play, Stop,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  VOICE_PRESETS, REVERB_PRESETS, AUDIO_MODES,
  getEffectsState, onEffectsChange,
  setVoicePreset, setReverbPreset, setAudioMode,
  setMicVolume, setEarMonitoring, resetEffects, applyFavorite,
  isEffectsSupported, isPresetAvailable, isLiteDevice,
  startPreview, stopPreview, isPreviewing,
} from "../lib/voiceEffects";

function Chip({ active, disabled, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`rounded-full px-3 py-1.5 text-[10px] font-black tracking-[0.15em] uppercase transition-colors ${
        active
          ? "bg-papaya text-ink"
          : disabled
            ? "bg-white/5 text-cream/25 cursor-not-allowed"
            : "bg-white/10 text-cream/80 hover:bg-white/20"
      }`}
    >
      {children}
    </button>
  );
}

export const VoiceEffectsPanel = ({ open, onClose }) => {
  const [fx, setFx] = useState(getEffectsState());
  const [previewing, setPreviewing] = useState(isPreviewing());
  const [favorites, setFavorites] = useState([]);
  const [favName, setFavName] = useState("");

  useEffect(() => onEffectsChange(setFx), []);

  useEffect(() => {
    if (!open) return;
    api.get("/voice/effects/favorites")
      .then(({ data }) => setFavorites(data || []))
      .catch(() => {});
  }, [open]);

  useEffect(() => () => { stopPreview(); }, []);

  if (!open) return null;

  const supported = isEffectsSupported();
  const lite = isLiteDevice();

  const pickVoice = (id) => {
    if (!isPresetAvailable(id)) {
      toast.error("This effect is unavailable on your device — using original voice.");
      return;
    }
    setVoicePreset(id);
  };

  const togglePreview = async () => {
    if (previewing) {
      stopPreview();
      setPreviewing(false);
    } else {
      try {
        await startPreview();
        setPreviewing(true);
        toast.info("Live preview on — speak to hear your effect. Use headphones to avoid feedback.");
      } catch (e) {
        toast.error("Microphone permission needed for preview.");
      }
    }
  };

  const saveFavorite = async () => {
    const name = favName.trim() ||
      `${VOICE_PRESETS.find((p) => p.id === fx.voice)?.label} + ${REVERB_PRESETS.find((p) => p.id === fx.reverb)?.label}`;
    try {
      const { data } = await api.post("/voice/effects/favorites", {
        name, voice: fx.voice, reverb: fx.reverb, mode: fx.mode, mic_volume: fx.micVolume,
      });
      setFavorites((f) => [data, ...f]);
      setFavName("");
      toast.success("Favourite saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save favourite.");
    }
  };

  const deleteFavorite = async (id) => {
    try {
      await api.delete(`/voice/effects/favorites/${id}`);
      setFavorites((f) => f.filter((x) => x.id !== id));
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-end justify-center" data-testid="voice-effects-panel">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="voice-effects-backdrop" />
      <div className="relative w-full max-w-lg max-h-[82dvh] overflow-y-auto bg-ink border-t border-white/15 rounded-t-3xl p-5 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <MagicWand size={18} className="text-papaya" weight="fill" />
            <p className="text-cream font-black uppercase tracking-[0.2em] text-xs">Voice Effects Library</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 text-cream flex items-center justify-center" data-testid="voice-effects-close">
            <X size={16} />
          </button>
        </div>
        <p className="text-cream/50 text-xs mb-4">
          Real-time voice changer, reverb & studio modes. Noise suppression, echo cancellation and auto gain stay on.
        </p>

        {!supported && (
          <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-3 mb-4" data-testid="voice-effects-unsupported">
            <p className="text-red-300 text-xs">Voice effects are unavailable on this device. Your original voice will be used.</p>
          </div>
        )}
        {supported && lite && (
          <div className="rounded-2xl bg-white/5 border border-white/10 p-3 mb-4" data-testid="voice-effects-lite-notice">
            <p className="text-cream/60 text-xs">Low-power device detected — pitch-based effects are disabled to keep your stream smooth. Noise suppression and echo cancellation remain active.</p>
          </div>
        )}

        {/* Voice presets */}
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold mb-2">Voice Changer</p>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="voice-presets-grid">
          {VOICE_PRESETS.map((p) => (
            <Chip
              key={p.id}
              active={fx.voice === p.id}
              disabled={!isPresetAvailable(p.id)}
              onClick={() => pickVoice(p.id)}
              testId={`voice-preset-${p.id}`}
            >
              {p.label}
            </Chip>
          ))}
        </div>

        {/* Reverb presets */}
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold mb-2">Reverb</p>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="reverb-presets-grid">
          {REVERB_PRESETS.map((p) => (
            <Chip
              key={p.id}
              active={fx.reverb === p.id}
              disabled={!supported}
              onClick={() => setReverbPreset(p.id)}
              testId={`reverb-preset-${p.id}`}
            >
              {p.label}
            </Chip>
          ))}
        </div>

        {/* Modes */}
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold mb-2">Mode</p>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="audio-modes-grid">
          {AUDIO_MODES.map((m) => (
            <Chip
              key={m.id}
              active={fx.mode === m.id}
              disabled={!supported}
              onClick={() => setAudioMode(m.id)}
              testId={`audio-mode-${m.id}`}
            >
              {m.label}
            </Chip>
          ))}
        </div>

        {/* Mic volume */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <SpeakerHigh size={14} className="text-cream/60" />
              <p className="text-cream/70 text-[10px] tracking-[0.3em] uppercase font-bold">Mic Volume</p>
            </div>
            <span className="text-cream/50 text-xs tabular-nums">{Math.round(fx.micVolume * 100)}%</span>
          </div>
          <input
            type="range" min="0" max="2" step="0.05"
            value={fx.micVolume}
            onChange={(e) => setMicVolume(parseFloat(e.target.value))}
            className="w-full accent-papaya"
            data-testid="mic-volume-slider"
          />
        </div>

        {/* Toggles row */}
        <div className="flex flex-wrap gap-2 mb-5">
          <button
            onClick={() => setEarMonitoring(!fx.earMonitor)}
            className={`flex-1 min-w-[130px] rounded-full py-2.5 text-[10px] font-black tracking-[0.15em] uppercase flex items-center justify-center gap-1.5 ${
              fx.earMonitor ? "bg-papaya text-ink" : "bg-white/10 text-cream/80"
            }`}
            data-testid="ear-monitor-toggle"
          >
            <Ear size={14} weight={fx.earMonitor ? "fill" : "regular"} />
            Ear Monitor {fx.earMonitor ? "On" : "Off"}
          </button>
          <button
            onClick={togglePreview}
            className={`flex-1 min-w-[130px] rounded-full py-2.5 text-[10px] font-black tracking-[0.15em] uppercase flex items-center justify-center gap-1.5 ${
              previewing ? "bg-red-500 text-white" : "bg-white/10 text-cream/80"
            }`}
            data-testid="voice-preview-toggle"
          >
            {previewing ? <Stop size={14} weight="fill" /> : <Play size={14} weight="fill" />}
            {previewing ? "Stop Preview" : "Live Preview"}
          </button>
          <button
            onClick={() => { resetEffects(); toast.success("Back to your original voice."); }}
            className="flex-1 min-w-[130px] rounded-full py-2.5 bg-white/10 text-cream/80 text-[10px] font-black tracking-[0.15em] uppercase flex items-center justify-center gap-1.5"
            data-testid="voice-effects-reset"
          >
            <ArrowCounterClockwise size={14} /> Reset
          </button>
        </div>

        {/* Favourites */}
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold mb-2">Favourites</p>
        <div className="flex gap-2 mb-3">
          <input
            value={favName}
            onChange={(e) => setFavName(e.target.value)}
            placeholder="Name this combo…"
            className="flex-1 bg-white/5 rounded-full px-4 py-2 text-cream text-xs border border-white/10 focus:border-papaya outline-none"
            data-testid="favorite-name-input"
          />
          <button
            onClick={saveFavorite}
            className="rounded-full bg-papaya text-ink px-4 py-2 text-[10px] font-black tracking-[0.15em] uppercase flex items-center gap-1"
            data-testid="favorite-save-btn"
          >
            <Heart size={12} weight="fill" /> Save
          </button>
        </div>
        {favorites.length === 0 ? (
          <p className="text-cream/40 text-xs" data-testid="favorites-empty">No favourites yet — dial in a sound and save it.</p>
        ) : (
          <div className="space-y-2" data-testid="favorites-list">
            {favorites.map((f) => (
              <div key={f.id} className="flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-3 py-2">
                <button
                  onClick={() => { applyFavorite(f); toast.success(`Applied “${f.name}”`); }}
                  className="flex-1 text-left"
                  data-testid={`favorite-apply-${f.id}`}
                >
                  <p className="text-cream text-xs font-bold truncate">{f.name}</p>
                  <p className="text-cream/40 text-[10px] uppercase tracking-wider">
                    {f.voice} · {f.reverb} · {f.mode}
                  </p>
                </button>
                <button
                  onClick={() => deleteFavorite(f.id)}
                  className="w-8 h-8 rounded-full hover:bg-red-500/20 flex items-center justify-center text-red-400"
                  data-testid={`favorite-delete-${f.id}`}
                >
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 text-cream/30">
          <Waveform size={12} />
          <p className="text-[10px] tracking-wide">
            Effects apply live to streams, rooms and recordings. On native apps they map to Agora's audio engine.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VoiceEffectsPanel;
